<template>
  <div>
    <!-- Exam Types List Card -->
    <b-card class="exam-types-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      <!-- Table Header -->
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 exam-type-title">{{ $t("Exam Categories") }}</h5>
          <small class="text-muted">{{
            $t("Manage and review exam categories")
          }}</small>
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2">
        <!-- Search Input -->
        <b-col xl="6" lg="6" md="5" sm="12" cols="12">
          <b-form-input
            v-model="searchQuery"
            :placeholder="$t('Search exam categories') + '...'"
            class="filter-control"
            @input="onSearchChange"
            debounce="500"
          />
        </b-col>
        <!-- Action Buttons -->
        <b-col xl="6" lg="6" md="7" sm="12" cols="12" class="text-right">
          <div
            class="d-flex gap-2 align-items-center justify-content-end flex-wrap"
          >
            <b-button
              v-if="rights.add"
              @click="AddOpen()"
              variant="gradient-primary"
              class="action-btn-filter"
              :disabled="request"
            >
              <b-spinner v-if="request" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              {{ $t("Add Exam Category") }}
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid -->
      <div class="table-scroll-wrapper">
        <b-table
          class="exam-types-table"
          :items="paginatedItems"
          :fields="fields"
          :busy="tableBusy"
          :responsive="false"
          hover
          show-empty
          :empty-text="$t(tableEmptyText)"
        >
          <template #table-busy>
            <tbody>
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + index"
                  class="skeleton-cell"
                >
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #cell(index)="data">
            <span
              class="text-muted font-weight-medium"
              :data-label="$t('S.No')"
            >
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </span>
          </template>

          <template #cell(examType)="data">
            <b-badge
              variant="light-primary"
              pill
              :data-label="$t('Exam Category')"
            >
              {{ data.item.examType }}
            </b-badge>
          </template>

          <template
            #cell(status)="data"
            v-if="statusVisible.includes(reportDomain)"
          >
            <div
              class="d-flex align-items-center justify-content-center"
              :data-label="$t('Status')"
            >
              <b-spinner
                v-if="statusLoading === data.item.id"
                small
                type="grow"
                class="mr-2"
              />
              <b-form-checkbox
                :checked="data.item.status === 'active'"
                switch
                @change="updateStatus(data.item.id, data.item.status)"
                :disabled="statusLoading === data.item.id"
                class="status-toggle"
              />
            </div>
          </template>

          <template #head()="scope">
            <div :class="scope.label == 'actions' && 'text-center'">
              {{ $t(scope.label) }}
            </div>
          </template>

          <template #cell(actions)="data">
            <div
              class="d-flex align-items-center justify-content-center flex-nowrap"
            >
              <b-button
                v-if="rights.edit && isExamDate"
                variant="gradient-primary"
                v-b-tooltip.hover.left
                :title="$t('Exam Dates')"
                size="sm"
                class="action-btn-table mr-1"
                @click="openDateSettings(data.item)"
                :disabled="
                  editLoading === data.item.id ||
                  deleteLoading === data.item.id ||
                  dateSettingLoading === data.item.id
                "
              >
                <b-spinner
                  v-if="dateSettingLoading === data.item.id"
                  small
                  type="grow"
                />
                <feather-icon v-else icon="CalendarIcon" size="16" />
              </b-button>
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.left
                :title="$t('Edit')"
                size="sm"
                class="action-btn-table"
                @click="Edit(data.item.id)"
                :disabled="
                  editLoading === data.item.id || deleteLoading === data.item.id
                "
              >
                <b-spinner
                  v-if="editLoading === data.item.id"
                  small
                  type="grow"
                />
                <feather-icon v-else icon="EditIcon" size="16" />
              </b-button>
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.left
                :title="$t('Delete')"
                size="sm"
                class="action-btn-outline ml-1"
                @click="Delete(data.item.id)"
                :disabled="
                  editLoading === data.item.id || deleteLoading === data.item.id
                "
              >
                <b-spinner
                  v-if="deleteLoading === data.item.id"
                  small
                  type="grow"
                />
                <feather-icon v-else icon="Trash2Icon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Sidebar for Add/Edit -->
    <b-sidebar
      id="add-exam-type-sidebar"
      :visible="visibility"
      bg-variant="white"
      backdrop
      shadow
      width="450px"
      no-header-close
      right
      @hidden="hideSideBar"
      class="exam-type-sidebar"
    >
      <div class="sidebar-header-modern">
        <div class="sidebar-header-content">
          <div class="sidebar-title-wrapper">
            <feather-icon
              :icon="sidebarTitle.includes('Add') ? 'PlusIcon' : 'EditIcon'"
              size="20"
              class="sidebar-title-icon"
            />
            <h5 class="mb-0 sidebar-title">{{ $t(sidebarTitle) }}</h5>
          </div>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="sidebar-close-btn"
            @click="hideSideBar()"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
        </div>
      </div>

      <div class="sidebar-body-modern">
        <b-form @submit.prevent class="sidebar-form-modern">
          <!-- Exam Category -->
          <b-form-group
            :label="$t('Exam Category')"
            class="modern-form-group"
            ref="name"
          >
            <b-form-input
              id="mc-first-name"
              :placeholder="$t('Enter exam category here')"
              name="classField"
              ref="name"
              v-model="myObj.examType"
              @input="CheckName()"
              :state="myObj.examType ? true : null"
              class="modern-input"
            />
            <div v-if="myObj.examType === ''" class="invalid-feedback d-block">
              {{ $t("Exam Category is required") }}
            </div>
          </b-form-group>

          <!-- Linked Exams -->
          <b-form-group :label="$t('Linked Exams')" class="modern-form-group">
            <v-select
              multiple
              :closeOnSelect="false"
              v-model="selectedIDs"
              :options="examOptions"
              label="examType"
              :reduce="(val) => val.id"
              :clearable="false"
              :disabled="editLoading"
              :placeholder="$t('Select linked exams')"
              class="modern-select"
            />
          </b-form-group>

          <!-- Weightage Section -->
          <div v-if="wObj && wObj.valueBool">
            <div v-if="wData.length === 0" class="text-center mb-3">
              <b-button
                @click="addValue()"
                variant="outline-primary"
                class="modern-btn-outline"
                size="sm"
              >
                <feather-icon icon="PlusIcon" size="16" class="mr-1" />
                {{ $t("Add Weightage") }}
              </b-button>
            </div>

            <div
              v-for="(item, index) in wData"
              :key="index"
              class="weightage-item mb-3"
            >
              <div
                class="weightage-header d-flex justify-content-between align-items-center mb-2"
              >
                <span class="weightage-label"
                  >{{ $t("Weightage") }} {{ index + 1 }}</span
                >
                <div>
                  <b-button
                    v-if="index === wData.length - 1"
                    @click="addValue()"
                    variant="outline-primary"
                    size="sm"
                    class="modern-btn-icon mr-1"
                  >
                    <feather-icon icon="PlusIcon" size="16" />
                  </b-button>
                  <b-button
                    @click="removeValue(index)"
                    variant="outline-danger"
                    size="sm"
                    class="modern-btn-icon"
                  >
                    <feather-icon icon="XIcon" size="16" />
                  </b-button>
                </div>
              </div>

              <b-form-group :label="$t('Classes')" class="modern-form-group">
                <v-select
                  multiple
                  :closeOnSelect="false"
                  v-model="item.clsIDs"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="getAvailableClasses(index)"
                  :clearable="false"
                  label="name"
                  :reduce="(val) => val.id"
                  :disabled="clsLoading"
                  :placeholder="$t('Select classes')"
                  class="modern-select"
                />
              </b-form-group>

              <b-form-group :label="$t('Weightage')" class="modern-form-group">
                <b-form-input
                  type="number"
                  v-model="item.weightage"
                  :placeholder="$t('Enter weightage')"
                  class="modern-input"
                />
              </b-form-group>
            </div>
          </div>
        </b-form>
      </div>

      <!-- Footer with Submit Button -->
      <div class="sidebar-footer-modern">
        <b-button
          type="submit"
          variant="gradient-primary"
          @click="Add()"
          :disabled="request"
          block
          class="modern-btn-submit"
        >
          <b-spinner v-if="request" small type="grow" class="mr-1" />
          <template v-else>
            <feather-icon icon="SaveIcon" size="16" class="mr-1" />
            <span>{{ $t(sidebarButton) }}</span>
          </template>
        </b-button>
      </div>
    </b-sidebar>

    <!-- Modal for Exam Date Settings -->
    <b-modal
      id="exam-date-settings-modal"
      v-model="dateModalVisible"
      :title="
        $t('Exam Dates') +
        (selectedExamType ? ' - ' + selectedExamType.examType : '')
      "
      title-class="font-medium-2 font-weight-bold"
      header-close-content="&times;"
      size="lg"
      hide-footer
      centered
      @hidden="closeDateModal"
    >
      <div class="p-1">
        <!-- Top Action Bar: Add Button -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0 text-muted font-weight-bold">
            {{ $t("Manage Exam Dates") }}
          </h6>
          <b-button
            v-if="!showAddDateForm"
            variant="gradient-primary"
            size="sm"
            @click="openAddDateForm"
            class="d-flex align-items-center"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-50" />
            <span>{{ $t("Add Exam Date") }}</span>
          </b-button>
        </div>

        <!-- Add Form Section -->
        <b-collapse v-model="showAddDateForm">
          <b-card
            no-body
            class="p-1 mb-1 shadow-none"
            style="
              background-color: #f8f9fa;
              border: 1px solid #e9ecef;
              border-radius: 8px;
            "
          >
            <h6 class="font-weight-bold mb-1 text-primary">
              {{ $t(dateForm.id ? "Edit Exam Date" : "Add Exam Date") }}
            </h6>
            <b-form @submit.prevent="saveExamDate">
              <b-row>
                <!-- Session Dropdown -->
                <b-col md="4" sm="12">
                  <b-form-group :label="$t('Session') + ' *'">
                    <v-select
                      v-model="dateForm.sessionID"
                      :options="filteredSessionsList"
                      label="session"
                      :reduce="(val) => val.id"
                      :placeholder="$t('Select Session')"
                      :clearable="false"
                    />
                  </b-form-group>
                </b-col>

                <!-- Date From -->
                <b-col md="4" sm="12">
                  <b-form-group :label="$t('Date From') + ' *'">
                    <flat-pickr
                      v-model="dateForm.dateFrom"
                      :config="datePickerConfig"
                      class="form-control"
                      :placeholder="$t('Select Date From')"
                    />
                  </b-form-group>
                </b-col>

                <!-- Date Till -->
                <b-col md="4" sm="12">
                  <b-form-group :label="$t('Date Till') + ' *'">
                    <flat-pickr
                      v-model="dateForm.dateTill"
                      :config="datePickerConfig"
                      class="form-control"
                      :placeholder="$t('Select Date Till')"
                    />
                  </b-form-group>
                </b-col>

                <!-- Result Date -->
                <b-col md="4" sm="12">
                  <b-form-group :label="$t('Result Date') + ' *'">
                    <flat-pickr
                      v-model="dateForm.resultDate"
                      :config="datePickerConfig"
                      class="form-control"
                      :placeholder="$t('Select Date Till')"
                    />
                  </b-form-group>
                </b-col>

                <!-- Next Term Date -->
                <b-col md="4" sm="12">
                  <b-form-group :label="$t('Next Term Date') + ' *'">
                    <flat-pickr
                      v-model="dateForm.nextTermDate"
                      :config="datePickerConfig"
                      class="form-control"
                      :placeholder="$t('Select Date Till')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="d-flex justify-content-end mt-25">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="mr-1"
                  @click="cancelAddDateForm"
                  :disabled="dateSaveLoading"
                >
                  {{ $t("Cancel") }}
                </b-button>
                <b-button
                  variant="gradient-primary"
                  size="sm"
                  type="submit"
                  :disabled="dateSaveLoading"
                >
                  <b-spinner
                    v-if="dateSaveLoading"
                    small
                    type="grow"
                    class="mr-50"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    size="14"
                    class="mr-50"
                  />
                  <span>{{ $t(dateForm.id ? "Update" : "Save") }}</span>
                </b-button>
              </div>
            </b-form>
          </b-card>
        </b-collapse>

        <!-- Exam Dates Table -->
        <div class="table-responsive border rounded mb-1">
          <table class="table table-hover mb-0 text-center">
            <thead class="thead-light">
              <tr>
                <th style="width: 70px">{{ $t("S.No") }}</th>
                <th>{{ $t("Session") }}</th>
                <th>{{ $t("Date From") }}</th>
                <th>{{ $t("Date Till") }}</th>
                <th style="width: 100px">{{ $t("Actions") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="dateSettingTableLoading">
                <td colspan="5" class="py-3 text-center">
                  <b-spinner small type="grow" class="mr-50" />
                  <span>{{ $t("Loading...") }}</span>
                </td>
              </tr>
              <tr v-else-if="!examDatesList || examDatesList.length === 0">
                <td colspan="5" class="py-3 text-center text-muted">
                  {{ $t("No exam dates found.") }}
                </td>
              </tr>
              <tr
                v-else
                v-for="(item, idx) in examDatesList"
                :key="item.examDate ? item.examDate.id : idx"
              >
                <td>{{ idx + 1 }}</td>
                <td class="font-weight-bold">
                  {{
                    item.session ? item.session.session || item.session : "-"
                  }}
                </td>
                <td>
                  {{ item.examDate ? formatDate(item.examDate.dateFrom) : "-" }}
                </td>
                <td>
                  {{ item.examDate ? formatDate(item.examDate.dateTill) : "-" }}
                </td>
                <td>
                  <div class="d-flex align-items-center justify-content-center">
                    <b-button
                      variant="gradient-primary"
                      size="sm"
                      class="action-btn-table mr-50"
                      v-b-tooltip.hover.left
                      :title="$t('Edit')"
                      @click="editExamDate(item)"
                      :disabled="
                        deleteDateLoading ===
                        (item.examDate ? item.examDate.id : item.id)
                      "
                    >
                      <feather-icon icon="EditIcon" size="14" />
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      class="action-btn-outline"
                      v-b-tooltip.hover.left
                      :title="$t('Delete')"
                      @click="
                        deleteExamDate(
                          item.examDate ? item.examDate.id : item.id
                        )
                      "
                      :disabled="
                        deleteDateLoading ===
                        (item.examDate ? item.examDate.id : item.id)
                      "
                    >
                      <b-spinner
                        v-if="
                          deleteDateLoading ===
                          (item.examDate ? item.examDate.id : item.id)
                        "
                        small
                        type="grow"
                      />
                      <feather-icon v-else icon="Trash2Icon" size="14" />
                    </b-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BFormGroup,
  BForm,
  BMediaAside,
  BAside,
  BSpinner,
  BCardTitle,
  BCardText,
  BSidebar,
  VBToggle,
  BInputGroupPrepend,
  BInputGroup,
  BFormTextarea,
  BModal,
  BCollapse,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions, mapGetters } from "vuex";
import axios from "axios";

export default {
  components: {
    flatPickr,
    BFormTextarea,
    BCard,
    BRow,
    BBadge,
    BTable,
    BCol,
    BFormInput,
    BButton,
    BSidebar,
    BFormGroup,
    BInputGroupPrepend,
    BInputGroup,
    BForm,
    BPagination,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    vSelect,
    BModal,
    BCollapse,
  },
  props: {},
  directives: {
    "b-tooltip": VBTooltip,
    "b-toggle": VBToggle,
    Ripple,
  },
  async created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.updateFields();
      this.LoadData();
      this.LoadAllExamTypes();
      this.LoadSettings();
    }
  },
  computed: {
    totalCount() {
      return this.totalItems || 0;
    },
    paginatedItems() {
      return this.items || [];
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = Math.min(
        (this.currentPage - 1) * this.perPage + 1,
        this.totalCount
      );
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    tableBusy() {
      return this.dataLoading;
    },
    tableEmptyText() {
      return this.tableBusy ? "" : "No exam categories found";
    },
    filteredSessionsList() {
      const existingSessionIds = (this.examDatesList || [])
        .filter((item) => {
          if (this.dateForm.id) {
            const recordId = item.examDate?.id ?? item.id;
            if (recordId === this.dateForm.id) {
              return false;
            }
          }
          return true;
        })
        .map((item) => {
          return item.examDate?.sessionID ?? item.session?.id ?? item.sessionID;
        })
        .filter((id) => id != null);

      return (this.sessionsList || []).filter((sess) => {
        return !existingSessionIds.includes(sess.id);
      });
    },
  },
  data() {
    return {
      rights: {},
      reportDomain: this.$store.state.report
        ? this.$store.state.report.replace(/_/g, "")
        : "",
      request: false,
      adding: false,
      sidebarTitle: "Add Exam Type",
      sidebarButton: "Save",
      dataLoading: false,
      visibility: false,
      logoloading: "",
      searchQuery: "",
      currentPage: 1,
      perPage: 30,
      totalPages: 1,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      typeOptions: [
        { text: "Exam", value: "exam" },
        { text: "Test", value: "test" },
      ],
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        mode: "range",
      },
      fields: [
        { label: "Exam Category", key: "examType", sortable: false },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          thStyle: { width: "150px", textAlign: "center" },
          tdClass: "actions-cell text-center",
        },
      ],
      items: [],
      totalItems: 0,
      allExamTypes: [], // Linked exams multiselect — rows from ExamTypes/LoadData (not ExamTypes? GET)
      myObj: {
        id: 0,
        examType: "",
        status: "active",
        campusID: this.$store.state.userData.cId,
        linkedIDs: "",
      },
      wObj: null,
      clsLoading: false,
      classes: [],
      classesOptions: [],
      wData: [],
      currentID: 0,
      editLoading: false,
      deleteLoading: null,
      statusLoading: null,
      selectedIDs: [],
      examOptions: [],
      searchTimeout: null,
      statusVisible: ["myskooliuss", "myskoolaba", "myskoolelixir"],
      dateSettingLoading: null,
      dateModalVisible: false,
      dateSettingTableLoading: false,
      dateSaveLoading: false,
      selectedExamType: null,
      examDatesList: [],
      sessionsList: [],
      showAddDateForm: false,
      dateForm: {
        id: 0,
        sessionID: null,
        dateFrom: null,
        dateTill: null,
        resultDate: null,
        nextTermDate: null,
        campusID: this.$store.state.userData.cId,
      },
      datePickerConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      isExamDate: false,
      deleteDateLoading: null,
    };
  },
  watch: {
    reportDomain: {
      immediate: true,
      handler() {
        this.updateFields();
      },
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    async openDateSettings(item) {
      this.selectedExamType = item;
      this.dateSettingLoading = item.id;
      this.dateModalVisible = true;
      this.showAddDateForm = false;
      this.resetDateForm();

      if (!this.sessionsList || this.sessionsList.length === 0) {
        await this.loadSessionsList();
      }

      await this.fetchExamDates(item.id);
      this.dateSettingLoading = null;
    },

    async fetchExamDates(examID) {
      this.dateSettingTableLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "ExamDates/LoadData?db=" +
            this.$store.state.userData.db +
            "&examID=" +
            examID,
          token: this.$store.state.userData.token,
        });
        const data = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : [];
        this.examDatesList = data;
      } catch (e) {
        console.error("Error loading exam dates:", e);
        this.examDatesList = [];
        this.$bvToast.toast(this.$t("Failed to load exam dates"), {
          title: this.$t("Error"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.dateSettingTableLoading = false;
      }
    },

    async loadSessionsList() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "Sessions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        const sessionsData = res?.data || res || [];
        this.sessionsList = Array.isArray(sessionsData) ? sessionsData : [];
      } catch (e) {
        console.error("Error loading sessions:", e);
        this.sessionsList = [];
      }
    },

    openAddDateForm() {
      this.resetDateForm();
      this.showAddDateForm = true;
    },

    editExamDate(item) {
      const d = item?.examDate || item || {};

      this.dateForm = {
        id: d.id || 0,
        sessionID: d.sessionID ?? item?.session?.id ?? null,
        dateFrom: d.dateFrom,
        dateTill: d.dateTill,
        resultDate: d.resultDate,
        nextTermDate: d.nextTermDate,
        campusID: d.campusID || this.$store.state.userData.cId,
        examTypeID: d.examTypeID || this.selectedExamType?.id,
      };
      this.showAddDateForm = true;
    },

    cancelAddDateForm() {
      this.showAddDateForm = false;
      this.resetDateForm();
    },

    resetDateForm() {
      this.dateForm = {
        id: 0,
        sessionID: null,
        dateFrom: null,
        dateTill: null,
        nextTermDate: null,
        resultDate: null,
        campusID: this.$store.state.userData.cId,
      };
    },

    closeDateModal() {
      this.dateModalVisible = false;
      this.showAddDateForm = false;
      this.resetDateForm();
      this.selectedExamType = null;
      this.examDatesList = [];
    },

    formatDate(val) {
      if (!val) return "-";
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return val;
        return d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch (e) {
        return val;
      }
    },

    async saveExamDate() {
      if (!this.dateForm.sessionID) {
        return this.$bvToast.toast(this.$t("Session is required."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
      if (!this.dateForm.dateFrom) {
        return this.$bvToast.toast(this.$t("Date From is required."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
      if (!this.dateForm.dateTill) {
        return this.$bvToast.toast(this.$t("Date Till is required."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }

      const fromTime = new Date(this.dateForm.dateFrom).getTime();
      const tillTime = new Date(this.dateForm.dateTill).getTime();
      if (fromTime > tillTime) {
        return this.$bvToast.toast(
          this.$t("Date From cannot be greater than Date Till."),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      }

      const payload = {
        id: this.dateForm.id || 0,
        examTypeID: this.selectedExamType.id,
        sessionID: this.dateForm.sessionID,
        dateFrom: this.dateForm.dateFrom,
        dateTill: this.dateForm.dateTill,
        resultDate: this.dateForm.resultDate || null,
        nextTermDate: this.dateForm.nextTermDate || null,
        campusID: this.dateForm.campusID || this.$store.state.userData.cId,
      };

      this.dateSaveLoading = true;
      try {
        let res;
        if (this.dateForm.id && this.dateForm.id > 0) {
          res = await this.put({
            url:
              this.$store.state.domain +
              "ExamDates/" +
              this.dateForm.id +
              "?db=" +
              this.$store.state.userData.db,
            body: payload,
            message: this.$t("Exam date updated successfully"),
            context: this,
            token: this.$store.state.userData.token,
          });
        } else {
          res = await this.post({
            url:
              this.$store.state.domain +
              "ExamDates?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId,
            body: payload,
            message: "Exam date added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
        }

        if (res) {
          this.showAddDateForm = false;
          this.resetDateForm();
          await this.fetchExamDates(this.selectedExamType.id);
        }
      } catch (error) {
        console.error("Error saving exam date:", error);
        this.$bvToast.toast(this.$t("Failed to save exam date."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.dateSaveLoading = false;
      }
    },

    async deleteExamDate(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });
      if (result.isConfirmed) {
        this.deleteDateLoading = id;
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "ExamDates/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          message: "Exam date removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          await this.fetchExamDates(this.selectedExamType.id);
        }
        this.deleteDateLoading = null;
      }
    },

    updateFields() {
      this.fields = [
        {
          label: "S.No",
          key: "index",
          sortable: false,
          thStyle: { width: "70px", minWidth: "70px", textAlign: "center" },
          tdClass: "text-center",
          tdStyle: { width: "70px", minWidth: "70px" },
        },
        {
          label: "Exam Category",
          key: "examType",
          sortable: false,
          thStyle: { minWidth: "200px" },
          tdStyle: { minWidth: "200px" },
        },
      ];

      // Add status column for myskooliuss
      if (this.statusVisible.includes(this.reportDomain)) {
        this.fields.push({
          key: "status",
          label: "Status",
          sortable: false,
          thStyle: { width: "110px", minWidth: "110px", textAlign: "center" },
          tdClass: "text-center",
          tdStyle: { width: "110px", minWidth: "110px" },
        });
      }

      this.fields.push({
        key: "actions",
        label: "Actions",
        sortable: false,
        thStyle: { width: "140px", minWidth: "140px", textAlign: "center" },
        tdClass: "actions-cell text-center",
        tdStyle: { width: "140px", minWidth: "140px" },
      });
    },

    async updateStatus(id, currentStatus) {
      // Toggle status: if currently active, make inactive, and vice versa
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      this.statusLoading = id;

      try {
        // Build query parameters
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          id: id,
          status: newStatus,
        });

        // Add userID if available (optional)
        if (this.$store.state.userData.userID) {
          params.append("userID", this.$store.state.userData.userID);
        }

        const config = {
          method: "put",
          url:
            this.$store.state.domain +
            "ExamTypes/SetStatus?" +
            params.toString(),
          headers: {
            Authorization: "bearer " + this.$store.state.userData.token,
            "Content-Type": "application/json",
          },
          data: null,
        };

        const response = await axios(config);

        // Check if the response indicates success
        if (response.data && response.data.status === "success") {
          // Show success message from API or fallback message
          const successMessage =
            response.data.message ||
            `Exam category ${
              newStatus === "active" ? "activated" : "deactivated"
            } successfully.`;
          this.$bvToast.toast(successMessage, {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          });

          // Update the item in the current list with the response data
          const item = this.items.find((el) => el.id === id);
          if (item && response.data.data) {
            item.status = response.data.data.status || newStatus;
          } else if (item) {
            item.status = newStatus;
          }

          // Also update in allExamTypes for dropdown
          const allItem = this.allExamTypes.find((el) => el.id === id);
          if (allItem && response.data.data) {
            allItem.status = response.data.data.status || newStatus;
          } else if (allItem) {
            allItem.status = newStatus;
          }
        } else {
          // Revert on failure
          this.$forceUpdate();
          const errorMessage =
            response.data?.message || "Failed to update status";
          this.$bvToast.toast(errorMessage, {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error updating status:", error);
        this.$bvToast.toast("Failed to update status", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        // Revert the toggle on error
        this.$forceUpdate();
      } finally {
        this.statusLoading = null;
      }
    },

    onSearchChange() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.LoadData();
      }, 500);
    },
    onPageChange(page) {
      this.currentPage = page;
      this.LoadData();
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.LoadData();
    },
    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect) {
          const toggle = this.$refs.pageSizeSelect.$el?.querySelector(
            ".vs__dropdown-toggle"
          );
          if (toggle) {
            toggle.blur();
            setTimeout(() => {
              document.body.click();
            }, 100);
          }
        }
      });
    },

    getAvailableClasses(currentIndex) {
      const selectedClassIds = this.wData.reduce((acc, item, index) => {
        if (index !== currentIndex) {
          acc.push(...item.clsIDs);
        }
        return acc;
      }, []);

      return this.classesOptions.filter(
        (cls) => !selectedClassIds.includes(cls.id)
      );
    },
    addValue() {
      this.wData.push({
        clsIDs: [],
        weightage: 0,
      });
    },
    removeValue(index) {
      this.wData.splice(index, 1);
    },

    async Edit(id) {
      this.editLoading = id;
      this.currentID = id;
      this.wData = [];

      if (this.wObj && this.wObj.valueBool) {
        var obj2 = {
          url:
            this.$store.state.domain +
            "ExamTypes/LoadWithWeightage?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&examID=" +
            id,
          token: this.$store.state.userData.token,
        };
        let res2 = await this.get(obj2);
        if (res2 == "not found" || Array.isArray(res2)) {
          this.$bvToast.toast(this.$t("Something went wrong!"), {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
        } else {
          this.myObj = res2.examType;
          this.wData = res2.weightData ? res2.weightData : [];
          this.selectedIDs = this.myObj.linkedIDs
            ? this.myObj.linkedIDs
                .split(",")
                .filter((el) => el)
                .map(Number)
            : [];
          this.examOptions = this.allExamTypes.filter((el) => el.id != id);
          this.adding = false;
          this.visibility = true;
          this.sidebarTitle = "Edit Exam Category";
          this.sidebarButton = "Update";
          var elem = this.$refs["name"];
          elem.state = undefined;
        }
      } else {
        var obj = {
          url:
            this.$store.state.domain +
            "ExamTypes/GetSelected?id=" +
            id +
            "&db=" +
            this.$store.state.userData.db,
          token: this.$store.state.userData.token,
        };
        let res = await this.get(obj);
        if (res == "NotFound" || Array.isArray(res)) {
          this.$bvToast.toast(this.$t("Something went wrong!"), {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
        } else {
          this.myObj = res;
          this.selectedIDs = this.myObj.linkedIDs
            ? this.myObj.linkedIDs
                .split(",")
                .filter((el) => el)
                .map(Number)
            : [];
          this.examOptions = this.allExamTypes.filter((el) => el.id != id);
          this.adding = false;
          this.visibility = true;
          this.sidebarTitle = "Edit Exam Category";
          this.sidebarButton = "Update";
          var elem = this.$refs["name"];
          elem.state = undefined;
        }
      }
      this.editLoading = false;
      this.currentID = 0;
    },
    AddOpen() {
      this.myObj = {
        id: 0,
        examType: "",
        status: "active",
        linkedIDs: "",
        campusID: this.$store.state.userData.cId,
      };
      this.wData = [];
      this.selectedIDs = [];
      this.examOptions = [...this.allExamTypes];
      this.adding = this.wObj && this.wObj.valueBool ? false : true;
      this.visibility = true;
      this.sidebarTitle = "Add Exam Category";
      this.sidebarButton = "Save";
      var elem = this.$refs["name"];
      if (elem) elem.state = undefined;
    },
    hideSideBar() {
      this.visibility = false;
    },

    async LoadSettings() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["exam-weightage", "exam-dates"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      this.wObj = res.find((el) => el.key == "exam-weightage");
      if (this.wObj && this.wObj.valueBool) this.LoadClasses();

      let obj = res.find((el) => el.key == "exam-dates");
      this.isExamDate = obj && obj.valueBool;
    },
    async LoadData() {
      this.dataLoading = true;

      try {
        const search = this.searchQuery?.trim() || "";
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          page: this.currentPage,
          pageSize: this.perPage,
        });

        if (search) {
          params.append("search", search);
        }

        const obj = {
          url:
            this.$store.state.domain +
            "ExamTypes/LoadData?" +
            params.toString(),
          token: this.$store.state.userData.token,
        };

        // Use axios directly to preserve pagination meta
        const res = await axios.get(obj.url, {
          headers: { Authorization: `Bearer ${obj.token}` },
        });

        // Normalize response (similar to HealthAssessment/inHouseDoctor)
        const payload = res?.data ?? res ?? {};
        const container = payload?.data;

        const list = this.extractExamTypesListFromLoadDataPayload(payload);

        // Extract meta (support many shapes)
        const pickNumber = (...vals) => {
          for (const v of vals) {
            const n = Number(v);
            if (v !== undefined && v !== null && !Number.isNaN(n)) return n;
          }
          return null;
        };

        const totalRecords =
          pickNumber(
            res?.totalCount,
            res?.totalRecords,
            res?.total,
            res?.data?.totalCount,
            res?.data?.totalRecords,
            res?.data?.total,
            res?.data?.pagination?.totalCount,
            res?.data?.pagination?.totalRecords,
            res?.pagination?.totalCount,
            res?.pagination?.totalRecords,
            payload?.totalCount,
            container?.totalCount,
            payload?.data?.totalCount,
            payload?.data?.total,
            payload?.total,
            container?.total,
            payload?.totalRecords,
            container?.totalRecords,
            payload?.data?.totalRecords,
            payload?.data?.pagination?.totalRecords,
            payload?.data?.pagination?.totalCount,
            container?.pagination?.totalRecords,
            container?.pagination?.totalCount,
            payload?.data?.meta?.totalRecords,
            payload?.data?.meta?.totalCount,
            list?.totalCount
          ) ?? list.length;

        const pageSizeFromApi =
          pickNumber(
            res?.pageSize,
            res?.page_size,
            res?.perPage,
            res?.data?.pageSize,
            res?.data?.page_size,
            res?.data?.perPage,
            res?.data?.pagination?.pageSize,
            res?.data?.pagination?.page_size,
            res?.pagination?.pageSize,
            res?.pagination?.page_size,
            payload?.pageSize,
            container?.pageSize,
            payload?.data?.pageSize,
            payload?.data?.pagination?.pageSize,
            payload?.data?.meta?.pageSize
          ) || this.perPage;

        const totalPages =
          pickNumber(
            res?.totalPages,
            res?.data?.totalPages,
            res?.data?.pagination?.totalPages,
            res?.pagination?.totalPages,
            payload?.totalPages,
            container?.totalPages,
            payload?.data?.totalPages,
            payload?.data?.pagination?.totalPages,
            payload?.data?.meta?.totalPages
          ) ||
          Math.ceil(
            (totalRecords || list.length) / (pageSizeFromApi || this.perPage)
          ) ||
          1;

        let pageFromApi =
          pickNumber(
            res?.page,
            res?.currentPage,
            res?.data?.page,
            res?.data?.currentPage,
            res?.data?.pagination?.page,
            res?.pagination?.page,
            payload?.page,
            container?.page,
            payload?.data?.page,
            payload?.data?.pagination?.page,
            payload?.data?.meta?.page
          ) ||
          this.currentPage ||
          1;
        if (pageFromApi > totalPages) pageFromApi = totalPages;
        if (pageFromApi < 1) pageFromApi = 1;

        // Apply data + meta
        this.items = list;
        this.totalItems = totalRecords || list.length;
        this.currentPage = pageFromApi;
        this.totalPages = totalPages;
        this.perPage = pageSizeFromApi || this.perPage;
      } catch (error) {
        this.$bvToast.toast("Failed to load exam categories", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        this.items = [];
        this.totalItems = 0;
      } finally {
        this.dataLoading = false;
      }
    },
    async LoadClasses() {
      this.clsLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      this.classes = await this.get(obj);
      this.classesOptions = this.classes.map((el) => el.cls);

      this.clsLoading = false;
    },

    /** Rows array from ExamTypes/LoadData response (same shapes as grid LoadData). */
    extractExamTypesListFromLoadDataPayload(payload) {
      const container = payload?.data;
      if (Array.isArray(container?.data)) return container.data;
      if (Array.isArray(container)) return container;
      if (Array.isArray(payload)) return payload;
      return [];
    },

    async LoadAllExamTypes() {
      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          page: 1,
          pageSize: 5000,
        });
        const url =
          this.$store.state.domain + "ExamTypes/LoadData?" + params.toString();
        const token = this.$store.state.userData.token;
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = res?.data ?? res ?? {};
        this.allExamTypes =
          this.extractExamTypesListFromLoadDataPayload(payload);
      } catch (e) {
        console.error(e);
        this.allExamTypes = [];
        this.$bvToast.toast(
          this.$t("Failed to load linked exams list.") ||
            "Failed to load linked exams list.",
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
      }
    },

    CheckName() {
      var elem = this.$refs["name"];
      if (this.myObj.examType.trim() === "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkValues() {
      let state = true;
      if (this.wObj && this.wObj.valueBool) {
        this.wData.forEach((el) => {
          let x = parseInt(el.weightage);
          if (isNaN(x) || x <= 0 || el.clsIDs.length == 0) {
            state = false;
          } else el.weightage = x;
        });
      }
      return state;
    },
    async Add() {
      this.CheckName();
      if (this.CheckName() == false) {
        return this.$bvToast.toast(this.$t("Please fill the form correctly."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } else if (this.checkValues() == false) {
        return this.$bvToast.toast(
          "Please add the weightage details correctly.",
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      } else {
        this.myObj.linkedIDs = this.selectedIDs.join(",");

        if (this.wObj && this.wObj.valueBool) {
          this.request = true;
          var status = await this.post({
            url:
              this.$store.state.domain +
              "ExamTypes/AddWithWeightage?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId,
            body: {
              examType: this.myObj,
              weightData: this.wData,
            },
            message: "Exam category saved successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;
          this.visibility = false;
          if (status) {
            this.LoadData();
            this.LoadAllExamTypes();
          }
        } else if (this.myObj.id == 0) {
          this.request = true;
          var status = await this.post({
            url:
              this.$store.state.domain +
              "ExamTypes?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId +
              "&terms=" +
              this.myObj.examType,
            body: this.myObj,
            message: "Exam category added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;
          this.visibility = false;
          if (status) {
            this.LoadData();
            this.LoadAllExamTypes();
          }
        } else {
          this.request = true;
          var status = await this.put({
            url:
              this.$store.state.domain +
              "ExamTypes/" +
              this.myObj.id +
              "?db=" +
              this.$store.state.userData.db,
            body: this.myObj,
            message: this.$t("Exam category updated successfully"),
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;
          this.visibility = false;
          if (status) {
            this.LoadData();
            this.LoadAllExamTypes();
          }
        }
      }
    },

    async Delete(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });
      if (result.isConfirmed) {
        this.deleteLoading = id;
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "ExamTypes/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          message: "Exam category removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.LoadData();
          this.LoadAllExamTypes();
        }
        this.deleteLoading = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

/* Keyframe animations - Soft landing */
@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-row-single-line {
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-right: -0.25rem;
  align-items: center;
  background: transparent !important;

  @media (min-width: 1200px) {
    flex-wrap: nowrap;
  }

  > [class*="col-"] {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    display: flex;
    align-items: center;
    background: transparent !important;
  }

  > [class*="col-"].text-right {
    justify-content: flex-end;
  }
}

.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

.action-btn-filter {
  height: 42px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.gap-2 {
  gap: 0.5rem;
}

.exam-types-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  .filter-row-single-line {
    background: transparent !important;
    padding: 0;
    margin-bottom: 1rem;
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

.exam-type-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;

  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;

    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }

  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }

  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

.page-size-select-compact {
  min-width: 75px;
  width: 75px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1.5 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  ::v-deep .vs__selected-options {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }

  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    overflow: visible !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }

  ::v-deep .vs__dropdown-option {
    padding: 0.5rem 0.75rem !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    font-size: 0.8125rem !important;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;

    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
    }

    &--highlight,
    &--selected {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600 !important;
    }

    &:active {
      background-color: #e5e7eb !important;
    }

    &--selected {
      background-color: #f3f0ff !important;
      color: #7c5dfa !important;
      font-weight: 600 !important;
    }
  }
}

.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;

  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }

  @media (max-width: 992px) {
    margin: 0;
    min-width: auto;
    width: 100%;
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;

    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }

    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }

    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

.table-scroll-wrapper {
  margin-top: 1rem;
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  border-radius: 12px;

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;

    &:hover {
      background: #94a3b8;
    }
  }

  @media (max-width: 768px) {
    margin-top: 0.5rem;
    // Force horizontal scroll on mobile
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;

    // Add shadow indicators for scroll
    position: relative;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      width: 30px;
      height: 100%;
      background: linear-gradient(
        to left,
        rgba(255, 255, 255, 0.9),
        transparent
      );
      pointer-events: none;
      z-index: 10;
    }
  }
}

.exam-types-table {
  margin-top: 0;
  border-radius: 12px;
  overflow: visible;
  border: 1px solid #e5e7eb;
  width: 100%;
  table-layout: auto;

  @media (max-width: 768px) {
    min-width: 520px; // Minimum width to show all columns properly
    width: 100%;
    table-layout: fixed;
  }

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;
    min-width: fit-content;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
    min-width: fit-content;
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;

      td {
        background: #f8fafc;
      }
    }

    &:nth-child(odd) {
      background: #ffffff;

      td {
        background: #ffffff;
      }
    }

    &:hover {
      background: #f1f5f9 !important;
      border-left-color: #7c5dfa;
      box-shadow: -2px 0 8px rgba(124, 93, 250, 0.1);
      z-index: 1;
      position: relative;

      td {
        background: transparent !important;
        color: #1e293b;
        font-weight: 500;
      }
    }
  }

  // Mobile Responsive Styles - Keep table structure, make scrollable
  @media (max-width: 768px) {
    ::v-deep {
      // Keep table structure, just adjust sizing
      table {
        width: 100%;
        min-width: 520px;
        table-layout: fixed;
        border-collapse: separate;
        border-spacing: 0;
      }

      th {
        padding: 0.75rem 0.5rem !important;
        font-size: 0.7rem !important;
        white-space: nowrap;
        position: sticky;
        top: 0;
        z-index: 10;
        background: linear-gradient(
          180deg,
          #ffffff 0%,
          #f8fafc 100%
        ) !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        overflow: hidden;
        text-overflow: ellipsis;
      }

      td {
        padding: 0.75rem 0.5rem !important;
        font-size: 0.8125rem !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: middle;
      }

      // Column width adjustments for mobile - Fixed widths
      th:nth-child(1),
      td:nth-child(1) {
        // S.No column
        width: 70px !important;
        min-width: 70px !important;
        max-width: 70px !important;
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
      }

      th:nth-child(2),
      td:nth-child(2) {
        // Exam Category column
        width: auto !important;
        min-width: 200px !important;
        white-space: normal;
        word-break: break-word;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
      }

      // Status column (3rd column if exists, 4th if not)
      th:nth-child(3),
      td:nth-child(3) {
        width: 110px !important;
        min-width: 110px !important;
        max-width: 110px !important;
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
      }

      // Actions column (last column)
      th:last-child,
      td:last-child {
        width: 140px !important;
        min-width: 140px !important;
        max-width: 140px !important;
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
      }

      // Adjust action buttons container for mobile
      .actions-cell {
        .d-flex {
          gap: 0.5rem !important;
          justify-content: center !important;
          flex-wrap: nowrap !important;
        }
      }

      // Adjust action buttons for mobile
      .action-btn-table {
        width: 36px !important;
        height: 36px !important;
        padding: 0 !important;
        min-width: 36px !important;
        flex-shrink: 0;
      }

      .action-btn-outline {
        width: 36px !important;
        height: 36px !important;
        padding: 0 !important;
        min-width: 36px !important;
        flex-shrink: 0;
      }

      // Status toggle mobile - ensure it fits
      .status-toggle {
        flex-shrink: 0;

        ::v-deep .custom-switch .custom-control-label::before {
          width: 2.25rem;
          height: 1.125rem;
        }

        ::v-deep .custom-switch .custom-control-label::after {
          width: calc(1.125rem - 4px);
          height: calc(1.125rem - 4px);
        }

        ::v-deep
          .custom-switch
          .custom-control-input:checked
          ~ .custom-control-label::after {
          transform: translateX(1.125rem);
        }
      }

      // Ensure status cell content is centered and doesn't overflow
      td:has(.status-toggle) {
        .d-flex {
          justify-content: center !important;
          align-items: center !important;
          gap: 0.5rem !important;
        }
      }
    }
  }

  // Tablet styles (768px - 992px)
  @media (min-width: 769px) and (max-width: 992px) {
    ::v-deep {
      th,
      td {
        padding: 0.625rem 0.5rem;
        font-size: 0.8125rem;
      }

      .action-btn-table {
        width: 32px;
        height: 32px;
        padding: 0;
      }
    }
  }

  // Small mobile (max 576px)
  @media (max-width: 576px) {
    ::v-deep {
      td {
        padding: 0.625rem 0.5rem 0.625rem 35% !important;
        font-size: 0.8125rem;
        min-height: 40px;

        &::before {
          width: 32%;
          font-size: 0.7rem;
        }
      }

      .action-btn-table {
        width: 36px;
        height: 36px;
        padding: 0;
        min-width: 36px;
      }

      .status-toggle {
        ::v-deep .custom-switch .custom-control-label::before {
          width: 2.25rem;
          height: 1.125rem;
        }

        ::v-deep .custom-switch .custom-control-label::after {
          width: calc(1.125rem - 4px);
          height: calc(1.125rem - 4px);
        }

        ::v-deep
          .custom-switch
          .custom-control-input:checked
          ~ .custom-control-label::after {
          transform: translateX(1.125rem);
        }
      }
    }
  }
}

.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;

  &:nth-child(1) {
    animation-delay: 0s;
  }

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  &:nth-child(4) {
    animation-delay: 0.45s;
  }

  &:nth-child(5) {
    animation-delay: 0.6s;
  }

  &:hover {
    background: transparent !important;
    border-left-color: transparent !important;
    box-shadow: none !important;
    transform: none !important;

    td {
      background: transparent !important;
      color: inherit !important;
      font-weight: normal !important;
    }
  }
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;

  &:nth-child(even) {
    background: #fafbfc !important;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.03) 50%,
      transparent 100%
    );
    animation: skeletonBorderGlow 2.5s ease-in-out infinite;
    pointer-events: none;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
    filter: blur(8px);
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.08) 30%,
      rgba(157, 122, 255, 0.12) 50%,
      rgba(124, 93, 250, 0.08) 70%,
      transparent 100%
    );
    animation: skeletonColorWave 2.5s ease-in-out infinite;
    border-radius: 10px;
  }
}

.skeleton-cell {
  &:nth-child(1) .skeleton-loader {
    width: 70%;
  }

  &:nth-child(2) .skeleton-loader {
    width: 100px;
    margin: 0 auto;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes skeletonColorWave {
  0%,
  100% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
  50% {
    opacity: 0.6;
    transform: scaleX(1);
  }
}

@keyframes skeletonBorderGlow {
  0%,
  100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(0.998);
  }
}

.actions-cell {
  min-width: 100px;
  max-width: 150px;
  vertical-align: middle !important;
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #ef4444 !important;
  color: #ef4444 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);

  &:hover:not(:disabled) {
    background: #fef2f2 !important;
    border-color: #dc2626 !important;
    color: #dc2626 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.modern-input {
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.75rem 0.875rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #1e293b;
  font-weight: 500;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
}

.btn-modern-primary {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.625rem 1.5rem;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  border: none;
  color: #fff;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6fff 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Hide the empty sidebar header
::v-deep #add-exam-type-sidebar .b-sidebar-header {
  display: none !important;
}

// Modern Sidebar Styling
.exam-type-sidebar {
  ::v-deep .b-sidebar-body {
    padding: 0 !important;
    overflow: hidden;
    max-height: 100vh;
  }
}

.sidebar-header-modern {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border-bottom: 2px solid #e0d8ff;
  padding: 1.25rem 1.5rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .sidebar-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 1;
  }

  .sidebar-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .sidebar-title-icon {
    color: #7c5dfa;
    stroke-width: 2.5;
  }

  .sidebar-title {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
    margin: 0;
  }

  .sidebar-close-btn {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid #e0d8ff;
    background: #fff;
    color: #64748b;
    transition: all 0.3s ease;

    &:hover {
      background: #f3f0ff;
      border-color: #b8a9ff;
      color: #7c5dfa;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(124, 93, 250, 0.15);
    }
  }
}

.sidebar-body-modern {
  padding: 1.5rem;
  background: #ffffff;
  min-height: calc(100vh - 200px);
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.sidebar-form-modern {
  .modern-form-group {
    margin-bottom: 1.5rem;

    label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #475569;
      margin-bottom: 0.5rem;
      display: block;
    }
  }

  .modern-input {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.625rem 0.875rem;
    min-height: 44px;
    background: #fff;
    transition: all 0.2s ease;
    color: #1e293b;
    font-weight: 500;
    width: 100%;

    &:hover {
      border-color: #b8a9ff;
    }

    &:focus {
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      outline: none;
    }

    &[readonly] {
      background: #f8fafc;
      color: #64748b;
    }

    &.is-invalid {
      border-color: #ef4444;
    }
  }

  .modern-select {
    ::v-deep .vs__dropdown-toggle {
      border-radius: 10px;
      border: 1.5px solid #e0d8ff;
      padding: 0.625rem 0.875rem;
      min-height: 44px;
      background: #fff;
      transition: all 0.2s ease;

      &:hover {
        border-color: #b8a9ff;
      }

      &:focus-within {
        border-color: #7c5dfa;
        box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      }
    }

    &.is-invalid {
      ::v-deep .vs__dropdown-toggle {
        border-color: #ef4444;
      }
    }
  }

  .modern-btn-outline {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.625rem 1rem;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }

  .modern-btn-icon {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.sidebar-footer-modern {
  padding: 1.25rem 1.5rem;
  background: #faf9ff;
  border-top: 2px solid #e0d8ff;
  position: sticky;
  bottom: 0;
  margin: 1.5rem -1.5rem -1.5rem -1.5rem;
  display: flex;
  justify-content: center;

  .modern-btn-submit {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.6rem 1.15rem;
    min-height: 42px;
    width: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.weightage-item {
  padding: 1rem;
  background: #faf9ff;
  border: 1.5px solid #e0d8ff;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #b8a9ff;
    box-shadow: 0 2px 8px rgba(124, 93, 250, 0.1);
  }

  .weightage-header {
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e0d8ff;
    margin-bottom: 1rem;
  }

  .weightage-label {
    font-size: 0.875rem;
    font-weight: 700;
    color: #2d1b69;
  }
}

.status-toggle {
  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #7c5dfa;
    border-color: #7c5dfa;
  }

  ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
    box-shadow: 0 0 0 0.2rem rgba(124, 93, 250, 0.25);
  }

  ::v-deep .custom-switch .custom-control-label::before {
    width: 2.5rem;
    height: 1.25rem;
    border-radius: 0.625rem;
    background-color: #cbd5e1;
    border-color: #cbd5e1;
    transition: all 0.3s ease;
  }

  ::v-deep .custom-switch .custom-control-label::after {
    width: calc(1.25rem - 4px);
    height: calc(1.25rem - 4px);
    border-radius: 50%;
    background-color: #fff;
    transition: all 0.3s ease;
  }

  ::v-deep
    .custom-switch
    .custom-control-input:checked
    ~ .custom-control-label::after {
    transform: translateX(1.25rem);
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  }
}
</style>
